<template>
  <!-- template must have 1 direct child, we wrap the contents in a <span>
       with "display: contents", making sure layout rendering is not affected -->
  <span style="display: contents">
    <v-card-text>
      <v-container>
        <v-row class="mt-n10" dense>
          <v-col cols="12">
            <term-loans-select-index />
          </v-col>
          <v-col cols="12">
            <uploader
              v-if="parseErrors.length === 0"
              dense
              :execute="(file) => $api.termLoans.uploadTermLoansSecurities(file)"
              hide-cancel-btn
              :upload-method="uploadMethod"
              @parse-errors="displayParseErrors($event)"
              @upload="populateFromUpload($event.securities)"
              @upload-method="uploadMethod = $event"
            >
              <template #description>
                <div>Expected column:</div>
                <div>Ticker or CUSIP</div>
              </template>
            </uploader>
            <upload-parse-errors
              v-else
              :errors="parseErrors"
              :headers="uploadParseErrorsHeaders"
              :upload-method="uploadMethod"
              @back="parseErrors = []"
            >
              <template #item="{ item }">
                <tr>
                  <td>{{ item.row }}</td>
                  <td>{{ item.errormsg }}</td>
                </tr>
              </template>
            </upload-parse-errors>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12">
            <multiple-security-selector
              :error-messages="errorMsgs['securities']"
              :securities.sync="contract.securities"
            />
          </v-col>
          <v-col class="text-end pr-4" cols="12">
            <strong>{{ contract.securities.length }}</strong> total securities
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
    <v-card-actions class="d-flex">
      <div class="d-flex flex-grow-1 justify-space-between align-end">
        <v-btn color="secondary" @click="$emit('back')">Back</v-btn>
        <v-btn color="primary" type="submit" @click="goToNextStep()">Next</v-btn>
      </div>
    </v-card-actions>
  </span>
</template>

<script lang="ts">
import Component, { mixins } from 'vue-class-component';
import { PropType } from 'vue';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import { Api } from '@/modules/common/types/api';
import { Security } from '@/modules/common/models';
import TermLoansSelectIndex from '@/modules/termloans/components/TermLoansSelectIndex.vue';
import MultipleSecuritySelector from '@/modules/common/components/MultipleSecuritySelector.vue';
import Uploader from '@/modules/common/components/Uploader.vue';
import UploadParseErrors from '@/modules/common/components/UploadParseErrors.vue';
import type { UploadMethod } from '@/modules/common/types/upload';
import ConfirmAutocomplete from '@/modules/open-loans/components/ConfirmAutocomplete.vue';
import { Contract } from '@/modules/termloans/models';

interface FormErrors {
  securities: string[];
}

@Component({
  props: {
    contract: {
      type: Object as PropType<Contract>,
      required: true,
    },
  },
  components: {
    TermLoansSelectIndex,
    MultipleSecuritySelector,
    Uploader,
    UploadParseErrors,
    ConfirmAutocomplete,
  },
  mixins: [validationMixin],
  validations: function (this: TermLoansDefineSecurities) {
    return {
      contract: {
        securities: {
          required,
        },
      },
    };
  },
})
export default class TermLoansDefineSecurities extends mixins(validationMixin) {
  public readonly uploadParseErrorsHeaders = [
    { text: 'Row', value: 'row' },
    { text: 'Error', value: 'errMsg' },
  ];

  // props
  public readonly contract!: Contract;

  public uploadMethod: UploadMethod | null = null;
  public parseErrors: Api.TermLoans.UploadErrorItem[] = [];

  public get errorMsgs(): FormErrors {
    const errors: FormErrors = {
      securities: [],
    };

    if (this.$v.contract?.securities?.$dirty) {
      if (!this.$v.contract.securities.required)
        errors.securities.push('please select at least one security.');
    }

    return errors;
  }

  public populateFromUpload(securities: Security[]): void {
    this.$emit('update:contract', { ...this.contract, securities });
  }

  public displayParseErrors(errors: Api.TermLoans.UploadErrorItem[]): void {
    this.parseErrors = errors;
  }

  public goToNextStep(): void {
    if (this.validateForm()) {
      this.$emit('next');
    }
  }

  public validateForm(): boolean {
    this.$v.$reset();
    this.$v.$touch();
    return !this.$v.$anyError;
  }
}
</script>

<style lang="scss" scoped></style>
